import type { Ref, ComputedRef } from 'vue'
import { ref, watch, computed, onBeforeUnmount, nextTick, inject } from 'vue'
import type { Instance, AnnotationsUnion } from 'pspdfkit'
import { isEqual, debounce } from 'lodash-es'

import type { CLIENT_TYPE } from '@lib/constants/cdp.const'
import { renderTemplate } from '@lib/util/handlebars'
import type { File } from '@openapi'
import type { OpenExternalUrlRequestPayload } from '@lib/sdk/payload.types'

interface UseAnnotationsReturnType {
  annotations: Ref<AnnotationsUnion[]>
  areAnnotationsModified: Ref<boolean>
  isAnnotationsInterpolating: Ref<boolean>
  isAnnotationsCleaning: Ref<boolean>
  updateAnnotations: () => Promise<void>
  cleanAnnotations: (instance: Instance, data?: Record<string, any>) => Promise<void>
  interpolateAnnotations: (i: Instance, data?: Record<string, any>) => Promise<void>
  getAllAnnotations: (instance: Instance) => Promise<AnnotationsUnion[]>
  resetAnnotationChanges: () => Promise<void>
}

export function useAnnotations(
  instance: Ref<Instance | undefined>,
  options: {
    clientType: (typeof CLIENT_TYPE)[keyof typeof CLIENT_TYPE]
    currentLinkAnnotation: Ref<AnnotationsUnion | null>
    currentFileId: ComputedRef<File['id'] | undefined>
    currentFilePageIndex: ComputedRef<number>
    emit: (eventType: string, ...args: any[]) => void
  },
): UseAnnotationsReturnType {
  const { clientType, currentLinkAnnotation, emit } = options
  const openExternalUrl = inject<(payload: OpenExternalUrlRequestPayload) => Promise<void>>('openExternalUrl')
  const initialAnnotations = ref<AnnotationsUnion[]>([])
  const annotations = ref<AnnotationsUnion[]>([])
  const isAnnotationsInterpolating = ref(false)
  const isAnnotationsCleaning = ref(false)
  const areAnnotationsModified = computed(() => {
    const getAnnotationState = (a: AnnotationsUnion) => ({
      id: a.id,
      type: a.type,
      pageIndex: a.pageIndex,
      boundingBox: a.boundingBox,
      rotation: a.rotation,
      opacity: a.opacity,
      strokeColor: a.strokeColor,
      text:
        a instanceof window.PSPDFKit.Annotations.TextAnnotation
          ? {
              ...a.text,
              value: normalizeText(a.text.value),
            }
          : undefined,
      lines: a instanceof window.PSPDFKit.Annotations.InkAnnotation ? a.lines : undefined,
    })

    // Compare normalized versions
    return !isEqual(initialAnnotations.value?.map(getAnnotationState), annotations.value?.map(getAnnotationState))
  })

  function normalizeText(text: string) {
    // remove the xml tags. Annotation HTML is wrapped in XML tags
    return text
      .replace(/(?:1\.0)+<body>/g, '<body>')
      .replace('<?xml version="1.0"?><body xmlns="http://www.w3.org/1999/xhtml">', '')
      .replace('<?xml version="1.0"?><body>', '')
      .replace('</body>', '')
      .replaceAll('<span>&ZeroWidthSpace;</span>', '')
      .replaceAll('<span style="color: #000000;"></span>', '')
      .trim()
  }

  async function resetAnnotationChanges() {
    if (instance.value) {
      annotations.value = await getAllAnnotations(instance.value)
      initialAnnotations.value = [...annotations.value]
    }
  }

  async function updateAnnotations() {
    if (instance.value) {
      annotations.value = await getAllAnnotations(instance.value)
    }
  }

  async function getAllAnnotations(instance: Instance): Promise<AnnotationsUnion[]> {
    try {
      const allAnnotations: AnnotationsUnion[] = []
      for (let pageIndex = 0; pageIndex < instance.totalPageCount; pageIndex++) {
        const pageAnnotations = await instance.getAnnotations(pageIndex)
        allAnnotations.push(...pageAnnotations)
      }
      return allAnnotations
    } catch (e) {
      console.error('[useAnnotations.ts] getAllAnnotations failed: ', e)
      return []
    }
  }

  const handleSelectAnnotation = debounce((data) => {
    instance.value?.setSelectedAnnotation(data.annotation)
    if (!currentLinkAnnotation.value) {
      currentLinkAnnotation.value = data.annotation
    } else {
      currentLinkAnnotation.value = null
      nextTick(() => {
        currentLinkAnnotation.value = data.annotation
      })
    }
  }, 10)

  const handleOpenOtherFile = debounce(
    async (annotationInfo: {
      id: string | null
      customData: {
        type: 'file' | 'url'
        fileId?: string
        url?: string
        pageIndex?: number
        newTab?: boolean
        startTime?: number
        tags?: string[]
        trackingId?: string
      }
      clientType: (typeof CLIENT_TYPE)[keyof typeof CLIENT_TYPE]
    }) => {
      emit('hotspotClicked', {
        fileInfo: { id: options.currentFileId.value!, pageIndex: options.currentFilePageIndex.value },
        annotationInfo,
      })

      if (annotationInfo.customData.type === 'url' && annotationInfo.customData.url) {
        const url = annotationInfo.customData.url
        const target = annotationInfo.customData.newTab ? '_blank' : '_self'

        try {
          if (openExternalUrl) {
            console.info('openExternalUrl', url, target)
            await openExternalUrl({ url, target })
          } else {
            console.info('window.open', url, target)
            window.open(url, target)
          }
        } catch (error) {
          console.error('Failed to open external URL:', error)
          // Fallback to window.open if platform handler fails
          window.open(url, target)
        }
        return
      }

      if (annotationInfo.customData.type === 'file' && annotationInfo.customData.fileId) {
        if (annotationInfo.customData.newTab && clientType !== 'dsr') {
          const basePath = window.location.pathname
          const newUrl = new URL(basePath, window.location.origin)
          newUrl.searchParams.set('file', annotationInfo.customData.fileId)

          if (annotationInfo.customData.pageIndex)
            newUrl.searchParams.set('page', annotationInfo.customData.pageIndex.toString())
          if (annotationInfo.customData.startTime)
            newUrl.searchParams.set('start-time', annotationInfo.customData.startTime.toString())

          window.open(newUrl.toString(), '_blank')
        } else {
          emit(
            'openFile',
            annotationInfo.customData.fileId,
            annotationInfo.customData.pageIndex,
            annotationInfo.customData.startTime,
            {
              isFromLinkAnnotation: true,
              currentFileId: options.currentFileId.value,
              currentFilePageIndex: options.currentFilePageIndex.value,
            },
          )
        }
      }
    },
    10,
  )

  // debounce your handlers -- they have a bug and dispatch twice
  const onAnnotationPress = (data: {
    annotation: AnnotationsUnion
    nativeEvent: Event
    preventDefault?: () => void
    selected: boolean
  }) => {
    if (clientType === 'admin') {
      if (data.annotation instanceof window.PSPDFKit.Annotations.LinkAnnotation) {
        // Only prevent default if the annotation is not already selected
        // This allows moving/resizing when already selected but prevents navigation when first clicking
        if (!data.selected) {
          data.preventDefault?.()
        }
        handleSelectAnnotation(data)
      }
    } else if (clientType === 'impact' || clientType === 'dsr') {
      if (data.annotation?.customData?.type === 'file' && data.annotation.customData.fileId) {
        const isPageOfSameFileOnSameTab =
          options.currentFileId.value === data.annotation.customData.fileId && !data.annotation.customData.newTab

        if (isPageOfSameFileOnSameTab) return // window.PSPDFKit.Actions.GoToAction({ pageIndex: data.customData.pageIndex || 0 }) will be invoked

        data.preventDefault?.()
        const annotationData = data.annotation.toJS()
        handleOpenOtherFile({
          id: annotationData.id,
          customData: annotationData.customData as {
            type: 'file'
            fileId: string
            pageIndex?: number
            newTab?: boolean
            startTime?: number
            tags?: string[]
            trackingId?: string
          },
          clientType,
        })
      } else if (data.annotation?.customData?.type === 'url' && data.annotation.customData.url) {
        data.preventDefault?.()
        const annotationData = data.annotation.toJS()
        handleOpenOtherFile({
          id: annotationData.id,
          customData: annotationData.customData as {
            type: 'url'
            url: string
            newTab?: boolean
            tags?: string[]
            trackingId?: string
          },
          clientType,
        })
      }
    }
  }
  async function cleanAnnotations(i: Instance) {
    try {
      console.info(`[useAnnotations.ts] cleanAnnotations called`)
      isAnnotationsCleaning.value = true

      await Promise.all(
        Array.from({ length: i.totalPageCount }, async (_, pageIndex) => {
          const annotations = await i.getAnnotations(pageIndex)
          for (const annotation of annotations) {
            if (annotation instanceof window.PSPDFKit.Annotations.TextAnnotation) {
              const initialValue = annotation.text.value
              const cleanedText = normalizeText(initialValue)

              if (cleanedText !== initialValue) {
                const updatedAnnotation = annotation.set('text', {
                  value: cleanedText,
                  format: annotation.text.format,
                })
                console.info(
                  `[useAnnotations.ts] cleaned annotation ${annotation.id} with value: ${cleanedText} was before: ${initialValue}`,
                )
                await i.update(updatedAnnotation)
              }
            }
          }
        }),
      )

      await updateAnnotations()
    } catch (e) {
      console.error('[useAnnotations.ts] cleanAnnotations failed: ', e)
    } finally {
      isAnnotationsCleaning.value = false
    }
  }

  async function interpolateAnnotations(i: Instance, data?: Record<string, any>) {
    try {
      console.info(`[useAnnotations.ts] interpolateAnnotations called with CRM data:`, data)
      isAnnotationsInterpolating.value = true

      await Promise.all(
        Array.from({ length: i.totalPageCount }, async (_, pageIndex) => {
          const annotations = await i.getAnnotations(pageIndex)
          for (const annotation of annotations) {
            if (annotation instanceof window.PSPDFKit.Annotations.TextAnnotation) {
              const initialValue = annotation.text.value
              const interpolatedText = normalizeText(renderTemplate(initialValue, data))
              if (interpolatedText !== initialValue) {
                const updatedAnnotation = annotation.set('text', {
                  value: interpolatedText,
                  format: annotation.text.format,
                })
                await i.update(updatedAnnotation)
              }
            }
          }
        }),
      )

      await updateAnnotations()
    } catch (e) {
      console.error('[useAnnotations.ts] interpolateAnnotations failed: ', e)
    } finally {
      isAnnotationsInterpolating.value = false
    }
  }

  // Set up a watcher to check for modifications whenever the instance changes
  const cleanup = watch(instance, async (newInstance, previous) => {
    if (newInstance) {
      previous && console.info('[useAnnotations.ts] instance changed -- watcher triggered')
      await resetAnnotationChanges()
      newInstance.addEventListener('annotations.change', updateAnnotations)
      newInstance.addEventListener('annotations.press', onAnnotationPress)
    }
  })

  onBeforeUnmount(() => {
    if (instance.value) {
      instance.value.removeEventListener('annotations.change', updateAnnotations)
      instance.value.removeEventListener('annotations.press', onAnnotationPress)
      cleanup?.()
    }
  })

  return {
    annotations,
    areAnnotationsModified,
    isAnnotationsInterpolating,
    isAnnotationsCleaning,
    updateAnnotations,
    interpolateAnnotations,
    cleanAnnotations,
    getAllAnnotations,

    resetAnnotationChanges,
  }
}
